/**
 * 带**超时 ＋ 中止**的 fetch（T-138）—— 让「启动期的静态资源拉取」**一定会 settle**。
 *
 * ## 为什么需要它（T-138 的取证结论，可复现）
 *
 * `src/i18n/index.ts` 的 `loadI18nConfig()` / `loadLocaleMessages()` 用裸 `fetch` 拉 `/locales/*.json`，
 * 而 `src/main.ts` 的 `await initI18n()` **在 `app.mount('#app')` 之前**。裸 `fetch` 在网络黑洞 /
 * 网关未就绪 / 后端未起时**既不 resolve 也不 reject**（连接挂起）⇒ `await` **永不返回** ⇒
 * `try` 与 `catch` **两个分支都不执行** ⇒ `mount` 永不发生 ⇒ **永久白屏，且控制台零错误**
 * （T-133 与 T-138 探针实测：挂起 `/locales/**` 后 `#app` 子节点数 6s 内恒为 0、
 * 控制台只有 vite 的 debug 两行、0 个 pageerror、`/locales/config.json` 请求 0 个完成）。
 *
 * 📌 **「挂起」不是「抛错」**：这正是既有 `try/catch`（main.ts）抓不到的原因 —— 它只在
 * `fetch` **reject** 时生效。超时把「永不 settle」转成一次**确定的 reject**，于是
 * **既有失败分支（catch → 仍然 mount）**被真正触达，语义与团队原本的意图一致。
 *
 * ## 设计约束
 * - **纯逻辑、可注入 `fetch`** —— 本项目**没有 jsdom / @vue/test-utils**，测试风格是「纯逻辑单测」，
 *   故这里不依赖任何 DOM/浏览器全局，`now`/定时器全部走注入的 `params.fetch` + 真实/假定时器。
 * - **一次性超时**：`setTimeout(abort, timeoutMs)` 到点 `abort()`；无论成功/失败都在 `finally`
 *   里 `clearTimeout`（否则会留下悬挂定时器 → vitest 报「未清理的定时器」/ 进程不退出）。
 * - **不吞错误**：超时抛出的错误带 `name === 'AbortError'`（浏览器 fetch 的既有语义）与可读 message，
 *   由调用方**显式记录**（不得静默）。
 */

/** 注入点：默认用全局 `fetch`；测试可注入假实现（含一个「永不 settle」的桩）。 */
export type FetchLike = typeof fetch

export interface FetchWithTimeoutOptions {
  /** 超时预算（毫秒）。到点后 `abort()` ⇒ 返回的 Promise 以 AbortError reject。 */
  timeoutMs: number
  /** 注入的 fetch 实现（默认 `globalThis.fetch`）。 */
  fetch?: FetchLike
  /** 注入的 AbortController 工厂（默认 `new AbortController()`）；测试可观察 `abort()` 是否被调用。 */
  createAbortController?: () => AbortController
}

/**
 * 发起 fetch，并在 `timeoutMs` 内**必定 settle**（成功 / 网络错误 / 超时中止）。
 *
 * @throws {Error} `name === 'AbortError'` —— 超时已中止（**不是**静默失败，调用方必须记录）
 * @throws {Error} 其它网络层错误原样向上抛（保持与裸 `fetch` 相同语义）
 */
export async function fetchWithTimeout(url: string, options: FetchWithTimeoutOptions): Promise<Response> {
  const { timeoutMs, fetch: fetchImpl, createAbortController } = options
  const doFetch: FetchLike = fetchImpl ?? globalThis.fetch
  const makeController = createAbortController ?? (() => new AbortController())

  // 无超时预算 ⇒ 等价于裸 fetch（防御：调用方误传 0/负数时不静默变成「立刻失败」）
  if (!(timeoutMs > 0)) return doFetch(url)

  const controller = makeController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)

  try {
    return await doFetch(url, { signal: controller.signal })
  } catch (error) {
    // 把我们自己触发的 abort 包装成可读的超时错误（保留 AbortError 语义，便于上层区分）
    if (timedOut) {
      const timeoutError = new Error(`请求超时：${timeoutMs}ms 内未完成（${url}）`)
      timeoutError.name = 'AbortError'
      throw timeoutError
    }
    throw error
  } finally {
    clearTimeout(timer)
  }
}
